// @flow
import * as React from 'react';
import styled from 'styled-components';
import CloseIcon from '../../icons/close';
import type { Color } from '../../colors';
import useDismissable from '../../hooks/use_dismissable';
import { Grid } from '../../layouts/grid';
import { Body, Link } from '../../typography';
import type { LinkProps } from '../../typography';
import { SecondaryButton } from '../buttons';
import type { ButtonProps } from '../buttons';
import { SafeThrower, SafeErrorBoundary } from '../../utils/safe_error';
import Theme from './theme';
import ErrorIcon from '../../icons/error';
import QuestionIcon from '../../icons/question';
import type { DataProvidesProps } from '../../utils/constants';
import { getDataProvidesProps } from '../../utils/constants';

type CallOutProps = {
  /**
   * Bold header text that appears above the body text.
   */
  headerText?: string,
  /**
   * Body text that appears on the left side of the call out. Helpful for providing context, especially when
   * combined with header text.
   */
  bodyText: string,
  /**
   * If set, an X icon will appear in the top right corner. Otherwise, the X icon will not appear.
   * When this X icon is clicked, the Call Out will disappear, be marked in localStorage by its key as hidden,
   * and will not re-appear until localStorage is cleared.
   */
  dismissableKey?: string,
  /**
   * Set of props that are passed directly to the Call to Action button on the right side of the Call Out.
   * If it is not set or is a falsey value, then the Call to Action button will not appear.
   */
  buttonProps?: ButtonProps,
  /**
   * Set of props that are passed directly to the Link at the bottom of the left side of the Call Out.
   * If it is not set or is a falsey value, then the Link will not appear.
   */
  linkProps?: LinkProps,
  ...DataProvidesProps,
};

type AbstractCallOutProps = CallOutProps & {
  icon: React.Node,
  backgroundColor: Color,
};

const CallOutWrapper = styled(Grid)`
  background: ${({ $backgroundColor }) => $backgroundColor};
  border-radius: 5px;
  padding: 4px 12px;
`;

const CallOut = (props: AbstractCallOutProps) => {
  const invalidProps =
    props.buttonProps && !(props.headerText || props.linkProps);
  if (invalidProps) {
    SafeThrower.safeThrow(
      'In order to display a Call to Action, you must also provide headerText and/or linkProps.'
    );
  }

  const [dismissed, setAsDismissed] = useDismissable(
    'callOut',
    props.dismissableKey
  );
  if (dismissed) {
    return <></>;
  }

  const mainContent = (
    <>
      {props.headerText && (
        <Body medium data-provides="call-out--header-text">
          {props.headerText}
        </Body>
      )}
      <Body data-provides="call-out--body-text">{props.bodyText}</Body>
      {props.linkProps && <Link {...props.linkProps} />}
    </>
  );
  const callToAction = props.buttonProps && (
    <SecondaryButton {...props.buttonProps} />
  );
  const closeIcon = (
    <DismissDiv data-provides="call-out--dismiss" onClick={setAsDismissed}>
      <CloseIcon color={Theme.default.closeIconColor} />
    </DismissDiv>
  );

  return (
    <CallOutWrapper
      container
      $backgroundColor={props.backgroundColor}
      {...getDataProvidesProps(props)}
    >
      <Grid item xs>
        <Grid container spacing={1}>
          <Grid item>{props.icon}</Grid>
          <Grid item xs>
            <Grid container justifyContent="space-between">
              <Grid item>{mainContent}</Grid>
              {callToAction && <Grid item>{callToAction}</Grid>}
            </Grid>
          </Grid>
        </Grid>
      </Grid>
      {props.dismissableKey && <Grid item>{closeIcon}</Grid>}
    </CallOutWrapper>
  );
};

const InformationalCallOut = (props: CallOutProps) => (
  <SafeErrorBoundary>
    <CallOut
      {...props}
      backgroundColor={Theme.informational.backgroundColor}
      icon={<QuestionIcon color={Theme.informational.iconColor} />}
    />
  </SafeErrorBoundary>
);
InformationalCallOut.displayName = 'InformationalCallOut';
InformationalCallOut.defaultProps = {
  'data-provides': 'informational-callout',
};

const WarningCallOut = (props: CallOutProps) => (
  <SafeErrorBoundary>
    <CallOut
      {...props}
      backgroundColor={Theme.warning.backgroundColor}
      icon={<ErrorIcon color={Theme.warning.iconColor} />}
    />
  </SafeErrorBoundary>
);
WarningCallOut.displayName = 'WarningCallOut';
WarningCallOut.defaultProps = { 'data-provides': 'warning-callout' };

const DismissDiv = styled.div`
  cursor: pointer;
`;

export { InformationalCallOut, WarningCallOut };
